import { useEffect, useRef, useState } from 'react';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import InfoTip from '@/components/InfoTip';
import clsx from 'clsx';
import {
  PERMISSION_CAPABILITIES,
  PERMISSION_ACCESS_LEVELS,
  CAPABILITY_LABELS,
  ACCESS_LABELS,
  SCOPE_LABELS,
  type PermissionAccess,
  type PermissionCapability,
  type PermissionScope,
  type RolePermission,
} from '@/domain/permissions';
import EditStudioModal from '@/screens/settings/EditStudioModal';
import EditTeamMemberModal from '@/screens/settings/EditTeamMemberModal';
import AddStudioModal from '@/screens/settings/AddStudioModal';
import MerchItemModal from '@/screens/settings/MerchItemModal';
import type { MerchItem, StudioId } from '@/domain/types';

type Tab = 'studios' | 'schema' | 'prompts' | 'permissions' | 'team' | 'merch' | 'targets' | 'audit';

// Next `merch-<studio>-<n>` id for a studio: one past the highest existing
// trailing index for that studio, so it never collides with a seeded or
// retired item. Keeps the keying convention (see seedMerchCatalogue) in sync.
function nextMerchItemId(merchItems: Record<string, MerchItem>, studioId: StudioId): string {
  const prefix = `merch-${studioId}-`;
  let max = -1;
  for (const item of Object.values(merchItems)) {
    if (item.studioId !== studioId || !item.id.startsWith(prefix)) continue;
    const n = Number(item.id.slice(prefix.length));
    if (Number.isInteger(n) && n > max) max = n;
  }
  return `${prefix}${max + 1}`;
}

const DAY_LABEL: Record<string, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

const SCHEMA_GROUPS: Array<{ title: string; fields: Array<{ key: string; type: string; source: string }> }> = [
  {
    title: 'Identity',
    fields: [
      { key: 'firstName', type: 'string', source: 'AI Lead Intake / agent capture' },
      { key: 'lastName', type: 'string', source: 'AI Lead Intake / agent capture' },
      { key: 'phone', type: 'AU E.164', source: 'AI capture / Front Desk' },
      { key: 'instagramHandle', type: 'string', source: 'Manychat / IG webhook' },
    ],
  },
  {
    title: 'Source and attribution',
    fields: [
      { key: 'source.channel', type: 'enum', source: 'first-touch event' },
      { key: 'source.campaignId', type: 'string', source: 'Meta campaign linker' },
      { key: 'source.firstTouchAt', type: 'ISO timestamp', source: 'webhook arrival' },
    ],
  },
  {
    title: 'Bookings and deposits',
    fields: [
      { key: 'bookings[]', type: 'Booking[]', source: 'Stripe webhook / agent booking' },
      { key: 'deposits[]', type: 'Deposit[]', source: 'Stripe Payment Link generation' },
      { key: 'creditOnFile.balanceAud', type: 'number', source: 'cancellation rollovers' },
    ],
  },
  {
    title: 'AI annotations',
    fields: [
      { key: 'leadScore', type: '0-100', source: 'AI scoring engine' },
      { key: 'toneTag', type: 'enum', source: 'AI tone detection' },
      { key: 'futureReadinessSignals[]', type: 'FutureSignal[]', source: 'Dialpad AI Recap' },
    ],
  },
];

export default function Settings() {
  const studios = useBrainStore((s) => s.studios);
  const agents = useBrainStore((s) => s.agents);
  const upsertStudio = useBrainStore((s) => s.upsertStudio);
  const upsertAgent = useBrainStore((s) => s.upsertAgent);
  const rolePermissions = useBrainStore((s) => s.rolePermissions);
  const setRolePermissions = useBrainStore((s) => s.setRolePermissions);
  const merchItems = useBrainStore((s) => s.merchItems);
  const upsertMerchItem = useBrainStore((s) => s.upsertMerchItem);
  const artists = useBrainStore((s) => s.artists);
  const reportingTargets = useBrainStore((s) => s.reportingTargets);
  const setArtistTarget = useBrainStore((s) => s.setArtistTarget);
  const setStudioReviewsTarget = useBrainStore((s) => s.setStudioReviewsTarget);
  const persona = useSessionStore((s) => s.persona);
  // 14 Jul ruling: targets live at the Georgina level (owner + Georgina edit).
  const canEditTargets = persona === 'owner' || persona === 'georgina';
  const [tab, setTab] = useState<Tab>('studios');
  const [targetStudioId, setTargetStudioId] = useState<StudioId | null>(null);
  const [merchStudioId, setMerchStudioId] = useState<StudioId | null>(null);
  const [merchModal, setMerchModal] = useState<{ mode: 'add' | 'edit'; item: MerchItem } | null>(
    null,
  );
  const [editingStudioId, setEditingStudioId] = useState<string | null>(null);
  const [editingAgentId, setEditingAgentId] = useState<string | null>(null);
  const [addingStudio, setAddingStudio] = useState(false);
  const [showArchivedStudios, setShowArchivedStudios] = useState(false);
  // Seeded once; safe because setRolePermissions is only ever called from this
  // component and rolePermissions is excluded from loadFixtures (see brainStore.ts).
  const [permDraft, setPermDraft] = useState<RolePermission[]>(rolePermissions);
  const [justSaved, setJustSaved] = useState(false);
  const savedTimer = useRef<number | null>(null);

  // Clear the "Saved ✓" timer on unmount so it never fires on a dead fiber.
  useEffect(() => () => {
    if (savedTimer.current) window.clearTimeout(savedTimer.current);
  }, []);

  const setPermScope = (idx: number, scope: PermissionScope) =>
    setPermDraft((rows) => rows.map((r, i) => (i === idx ? { ...r, scope } : r)));

  const setPermAccess = (idx: number, cap: PermissionCapability, level: PermissionAccess) =>
    setPermDraft((rows) =>
      rows.map((r, i) =>
        i === idx ? { ...r, capabilities: { ...r.capabilities, [cap]: level } } : r,
      ),
    );

  const handleSavePermissions = () => {
    setRolePermissions(permDraft);
    setJustSaved(true);
    if (savedTimer.current) window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setJustSaved(false), 1500);
  };

  const editingStudio = editingStudioId ? studios[editingStudioId] : undefined;
  const editingAgent = editingAgentId ? agents[editingAgentId] : undefined;
  const visibleStudios = Object.values(studios).filter((s) =>
    showArchivedStudios ? s.archived : !s.archived,
  );

  // Merch catalogue is managed per active studio. Default to the first active
  // one; fall back to whatever the picker holds if that studio was archived.
  const activeStudios = Object.values(studios).filter((s) => !s.archived);
  const merchStudio =
    (merchStudioId ? studios[merchStudioId] : undefined) ?? activeStudios[0];
  const merchList = merchStudio
    ? Object.values(merchItems)
        .filter((m) => m.studioId === merchStudio.id)
        .sort((a, b) => a.name.localeCompare(b.name))
    : [];

  return (
    <section className="px-8 py-8" data-testid="settings">
      <header className="max-w-6xl">
        <p className="label-small-caps text-gold-bright">PRD §12.18</p>
        <h1 className="heading-display text-3xl text-cream mt-2">Settings &amp; Brain Schema</h1>
        <p className="font-serif text-cream-soft mt-3">
          Show what the App holds. Validate the data shape during play-testing.
        </p>
      </header>

      <nav className="mt-6 flex gap-2 flex-wrap border-b border-gold-dim/20 pb-2 max-w-6xl">
        {(
          [
            ['studios', 'Studios'],
            ['schema', 'Schema explorer'],
            ['prompts', 'AI prompts'],
            ['permissions', 'Permissions'],
            ['team', 'Team management'],
            ['merch', 'Merch & aftercare'],
            ['targets', 'Targets'],
            ['audit', 'Audit trail'],
          ] as Array<[Tab, string]>
        ).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={clsx(
              'px-4 py-1.5 text-xs uppercase tracking-widest rounded-full border transition',
              tab === id
                ? 'border-gold-bright text-gold-bright bg-gold/10'
                : 'border-transparent text-cream-soft hover:text-cream',
            )}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="mt-6 max-w-6xl">
        {tab === 'studios' ? (
          <>
            <div className="flex items-start justify-between gap-4 mb-4">
              <p className="font-serif text-cream-soft">
                These locations gate every studio picker in the app. The getting-here notes and
                prep reminders feed straight into the SMS confirmation sequence sent to each booking
                24 hours out.
              </p>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowArchivedStudios((v) => !v)}
                  className="btn-quiet text-xs"
                  data-testid="studio-show-archived"
                >
                  {showArchivedStudios ? 'Hide archived' : 'Show archived'}
                </button>
                <button
                  type="button"
                  onClick={() => setAddingStudio(true)}
                  className="btn-primary text-xs"
                  data-testid="studio-add"
                >
                  Add studio
                </button>
              </div>
            </div>
            {visibleStudios.length === 0 ? (
              <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                {showArchivedStudios ? 'No archived studios.' : 'No active studios.'}
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {visibleStudios.map((studio) => (
                  <article
                    key={studio.id}
                    className="panel p-4 space-y-3"
                    data-testid={`studio-card-${studio.id}`}
                  >
                    <header className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-inter text-[10px] uppercase tracking-widest text-gold-bright">
                          {studio.brand}
                        </p>
                        <h3 className="heading-serif text-lg text-cream mt-1">{studio.name}</h3>
                        <p className="font-mono text-[11px] text-cream-soft mt-1">
                          {studio.address}, {studio.postcode} · {studio.state} ·{' '}
                          {studio.brandedNumber}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {studio.archived ? (
                          <>
                            <span className="pill-cream text-[10px]">Archived</span>
                            <button
                              type="button"
                              onClick={() => upsertStudio({ ...studio, archived: false })}
                              className="btn-quiet text-[10px]"
                              data-testid={`studio-restore-${studio.id}`}
                            >
                              Restore
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => setEditingStudioId(studio.id)}
                              className="btn-quiet text-[10px]"
                              data-testid={`studio-edit-${studio.id}`}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => upsertStudio({ ...studio, archived: true })}
                              className="btn-quiet text-[10px]"
                              data-testid={`studio-archive-${studio.id}`}
                            >
                              Archive
                            </button>
                          </>
                        )}
                      </div>
                    </header>

                    <section>
                      <p className="label-small-caps text-gold-bright">Opening hours</p>
                      <ul className="mt-2 grid grid-cols-7 gap-1 text-center">
                        {(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const).map((d) => {
                          const slot = studio.operatingHours[d];
                          return (
                            <li
                              key={d}
                              className="bg-ink-dark border border-gold-dim/20 rounded px-1.5 py-1"
                            >
                              <p className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
                                {DAY_LABEL[d]}
                              </p>
                              <p className="font-mono text-[10px] text-cream mt-0.5">
                                {slot ? `${slot.open}–${slot.close}` : 'Closed'}
                              </p>
                            </li>
                          );
                        })}
                      </ul>
                    </section>

                    {studio.gettingHere ? (
                      <section>
                        <p className="label-small-caps text-gold-bright">Getting here</p>
                        <dl className="mt-2 space-y-1 text-xs">
                          {studio.gettingHere.parking ? (
                            <div>
                              <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
                                Parking
                              </dt>
                              <dd className="font-serif text-cream-soft mt-0.5">
                                {studio.gettingHere.parking}
                              </dd>
                            </div>
                          ) : null}
                          {studio.gettingHere.transport ? (
                            <div>
                              <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
                                Transport
                              </dt>
                              <dd className="font-serif text-cream-soft mt-0.5">
                                {studio.gettingHere.transport}
                              </dd>
                            </div>
                          ) : null}
                          {studio.gettingHere.directions ? (
                            <div>
                              <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
                                Wayfinding
                              </dt>
                              <dd className="font-serif text-cream-soft mt-0.5">
                                {studio.gettingHere.directions}
                              </dd>
                            </div>
                          ) : null}
                        </dl>
                      </section>
                    ) : null}

                    {studio.prepNotes ? (
                      <section>
                        <p className="label-small-caps text-gold-bright">Prep reminders</p>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {studio.prepNotes.eatBeforehand ? (
                            <span className="pill-cream text-[10px]">Eat beforehand</span>
                          ) : null}
                          {studio.prepNotes.hydrate ? (
                            <span className="pill-cream text-[10px]">Hydrate</span>
                          ) : null}
                        </div>
                        {studio.prepNotes.custom ? (
                          <p className="font-serif text-cream-soft text-xs mt-2">
                            {studio.prepNotes.custom}
                          </p>
                        ) : null}
                      </section>
                    ) : null}

                    <footer className="pt-2 border-t border-gold-dim/15 flex items-center justify-between">
                      <span className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
                        Manager · {agents[studio.managerId]?.firstName ?? studio.managerId}
                      </span>
                      <span className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
                        {studio.artistCount} artists
                      </span>
                    </footer>
                  </article>
                ))}
              </div>
            )}
            <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mt-4">
              Edits save instantly to the brain and propagate to every studio picker, confirmation
              template and front-desk display in this session.
            </p>
          </>
        ) : null}
        {tab === 'schema' ? (
          <div className="grid grid-cols-2 gap-4">
            {SCHEMA_GROUPS.map((group) => (
              <div key={group.title} className="panel p-4">
                <h3 className="heading-serif text-base text-cream mb-2">{group.title}</h3>
                <ul className="space-y-2 text-xs">
                  {group.fields.map((f) => (
                    <li key={f.key} className="border-b border-gold-dim/15 last:border-0 pb-2">
                      <p className="font-mono text-cream">{f.key}</p>
                      <p className="font-inter text-[10px] text-gold-bright uppercase mt-1">{f.type}</p>
                      <p className="font-serif text-cream-soft mt-1">Source: {f.source}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : null}
        {tab === 'prompts' ? (
          <div className="panel p-4">
            <h3 className="heading-serif text-base text-cream mb-2">AI Lead Intake brand voice</h3>
            <pre className="font-mono text-xs text-cream bg-ink-black border border-gold-dim/30 rounded p-3 whitespace-pre-wrap">
              You are the AI lead intake agent for Tattoo Vault. You speak in casual Australian
              vernacular. Use mate / awesome / sweet / no worries naturally for under-30s. Skip the
              vernacular for over-40s, stay warm and direct. Never over-promise on pricing without
              referencing the team. Hand off any cancellation policy question to a human.
            </pre>
            <p className="font-inter text-[10px] text-cream-soft mt-3 uppercase tracking-widest">
              Read-only in prototype. Owners + Georgina can edit in production.
            </p>
          </div>
        ) : null}
        {tab === 'permissions' ? (
          <div className="panel p-4">
            <div className="flex items-center justify-between mb-3 gap-4">
              <p className="font-serif text-cream-soft text-sm">
                Set each role&apos;s access level per capability and its scope. Saved configuration
                is held in the brain for this session.
              </p>
              <div className="flex items-center gap-3 shrink-0">
                {justSaved ? (
                  <span
                    className="font-inter text-[10px] text-mint uppercase tracking-widest"
                    data-testid="permissions-saved-indicator"
                  >
                    Saved ✓
                  </span>
                ) : null}
                <button
                  type="button"
                  onClick={handleSavePermissions}
                  className="btn-primary text-xs"
                  data-testid="permissions-save"
                >
                  Save
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-ink-soft/40">
                  <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                    <th className="text-left px-3 py-2">Role</th>
                    <th className="text-left px-3 py-2">Scope</th>
                    {PERMISSION_CAPABILITIES.map((c) => (
                      <th key={c} className="px-2 py-2 text-center whitespace-nowrap">
                        {CAPABILITY_LABELS[c]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {permDraft.map((row, rowIdx) => (
                    <tr key={row.role} className="border-t border-gold-dim/15">
                      <td className="px-3 py-2 font-inter text-cream whitespace-nowrap">{row.role}</td>
                      <td className="px-3 py-2">
                        <select
                          value={row.scope}
                          onChange={(e) => setPermScope(rowIdx, e.target.value as PermissionScope)}
                          className="bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-1 text-xs text-cream"
                          data-testid={`permission-scope-${rowIdx}`}
                        >
                          {(Object.keys(SCOPE_LABELS) as PermissionScope[]).map((s) => (
                            <option key={s} value={s}>
                              {SCOPE_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      </td>
                      {PERMISSION_CAPABILITIES.map((c) => (
                        <td key={c} className="px-2 py-2 text-center">
                          <select
                            value={row.capabilities[c]}
                            onChange={(e) =>
                              setPermAccess(rowIdx, c, e.target.value as PermissionAccess)
                            }
                            className="bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-1 text-xs text-cream"
                            data-testid={`permission-${rowIdx}-${c}`}
                          >
                            {PERMISSION_ACCESS_LEVELS.map((lvl) => (
                              <option key={lvl} value={lvl}>
                                {ACCESS_LABELS[lvl]}
                              </option>
                            ))}
                          </select>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        {tab === 'team' ? (
          <div className="panel p-4">
            <table className="w-full text-sm">
              <thead className="bg-ink-soft/40">
                <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                  <th className="text-left px-3 py-2">Member</th>
                  <th className="text-left px-3 py-2">Role</th>
                  <th className="text-left px-3 py-2">Assigned studios</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {Object.values(agents).map((a) => (
                  <tr key={a.id} className="border-t border-gold-dim/15">
                    <td className="px-3 py-2 font-inter text-cream">
                      {a.firstName} {a.lastName}
                    </td>
                    <td className="px-3 py-2 font-inter text-cream-soft text-xs uppercase tracking-widest">
                      {a.role}
                    </td>
                    <td className="px-3 py-2 text-cream-soft text-xs">
                      {a.assignedStudios.length === 0
                        ? '...'
                        : a.assignedStudios.map((s) => studios[s]?.name ?? s).join(', ')}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => setEditingAgentId(a.id)}
                        className="btn-quiet text-[10px]"
                        data-testid={`team-edit-${a.id}`}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {tab === 'merch' ? (
          <>
            <div className="flex items-start justify-between gap-4 mb-4">
              <p className="font-serif text-cream-soft">
                Where the checkout picker gets its items and prices. Per studio &mdash; each location
                manages its own list. Active items show in the make-sale merch picker; retired items
                stay on past sales but drop out of checkout. No commission on any of it.
              </p>
            </div>
            {!merchStudio ? (
              <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                No active studios to manage a catalogue for.
              </p>
            ) : (
              <div className="panel p-4 space-y-4" data-testid="merch-manager">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <p className="label-small-caps text-gold-bright">Merch &amp; aftercare</p>
                    <select
                      className="bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-1.5 text-sm text-cream"
                      value={merchStudio.id}
                      onChange={(e) => setMerchStudioId(e.target.value as StudioId)}
                      data-testid="merch-studio-select"
                    >
                      {activeStudios.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setMerchModal({
                        mode: 'add',
                        item: {
                          id: nextMerchItemId(merchItems, merchStudio.id),
                          studioId: merchStudio.id,
                          name: '',
                          category: 'aftercare',
                          priceAud: 0,
                          status: 'active',
                        },
                      })
                    }
                    className="btn-primary text-xs"
                    data-testid="merch-add"
                  >
                    + Add item
                  </button>
                </div>

                {merchList.length === 0 ? (
                  <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                    No items yet. Add the first one for {merchStudio.name}.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left font-inter text-[10px] uppercase tracking-widest text-cream-faint border-b border-gold-dim/20">
                          <th className="py-2 pr-3 font-normal">Item</th>
                          <th className="py-2 pr-3 font-normal">Category</th>
                          <th className="py-2 pr-3 font-normal text-right">Price</th>
                          <th className="py-2 pr-3 font-normal">Status</th>
                          <th className="py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {merchList.map((item) => (
                          <tr
                            key={item.id}
                            className="border-b border-gold-dim/10"
                            data-testid={`merch-row-${item.id}`}
                          >
                            <td className="py-2 pr-3 text-cream">{item.name}</td>
                            <td className="py-2 pr-3 text-cream-soft capitalize">{item.category}</td>
                            <td className="py-2 pr-3 text-right font-mono text-cream">
                              ${item.priceAud}
                            </td>
                            <td className="py-2 pr-3">
                              {item.status === 'active' ? (
                                <span className="pill-mint text-[10px]">Active</span>
                              ) : (
                                <span className="pill-cream text-[10px]">Retired</span>
                              )}
                            </td>
                            <td className="py-2 text-right whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() =>
                                  upsertMerchItem({
                                    ...item,
                                    status: item.status === 'active' ? 'retired' : 'active',
                                  })
                                }
                                className="btn-quiet text-[10px] mr-2"
                                data-testid={`merch-toggle-${item.id}`}
                              >
                                {item.status === 'active' ? 'Retire' : 'Restore'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setMerchModal({ mode: 'edit', item })}
                                className="btn-quiet text-[10px]"
                                data-testid={`merch-edit-${item.id}`}
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        ) : null}
        {tab === 'targets' ? (
          (() => {
            const activeStudioList = Object.values(studios).filter((s) => !s.archived);
            const sid = (targetStudioId ?? activeStudioList[0]?.id ?? null) as StudioId | null;
            const studioArtists = Object.values(artists)
              .filter((a) => !a.archived && a.studioId === sid)
              .sort((a, b) => a.displayName.localeCompare(b.displayName));
            const targetFor = (artistId: string) =>
              reportingTargets.artistOverrides[artistId] ?? reportingTargets.studioDefault;
            const reviewsFor = (studioId: StudioId) =>
              reportingTargets.reviewsByStudio[studioId] ?? reportingTargets.reviewsDefault;
            const inputCls =
              'w-24 rounded border border-gold-dim/30 bg-ink-black px-2 py-1 text-sm font-mono text-cream disabled:opacity-50 disabled:cursor-not-allowed';
            return (
              <div className="space-y-4" data-testid="settings-targets">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <p className="label-small-caps flex items-center gap-1">
                      Weekly targets
                      <InfoTip>
                        The per-studio, per-artist weekly targets the reports measure against - the sheet the ops team
                        builds by hand today. Sessions and dollars feed the Targets board; deposit-sessions is Zack&apos;s
                        DEPOSITS number (booked sessions with a deposit); reviews is the studio&apos;s weekly review goal.
                        Edits save live and flow straight into the reports.
                      </InfoTip>
                    </p>
                    <p className="font-serif text-cream-soft text-sm mt-1">
                      {canEditTargets
                        ? 'Set the weekly goals per studio and artist.'
                        : 'Read-only. Only Georgina and owners can edit targets.'}
                    </p>
                  </div>
                  <select
                    value={sid ?? ''}
                    onChange={(e) => setTargetStudioId(e.target.value as StudioId)}
                    data-testid="targets-config-studio-select"
                    aria-label="Studio"
                    className="rounded-md border border-gold-dim/30 bg-ink-soft/40 px-3 py-1.5 text-xs font-inter text-cream-soft"
                  >
                    {activeStudioList.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                {!canEditTargets ? (
                  <p
                    className="rounded-md border border-gold-dim/30 bg-ink-soft/40 px-3 py-2 font-inter text-[11px] text-cream-soft"
                    data-testid="targets-config-readonly-note"
                  >
                    You are viewing as {persona ?? 'a guest'}. Switch to Georgina or an owner to edit.
                  </p>
                ) : null}

                {sid ? (
                  <div className="panel p-4 space-y-4">
                    <div className="flex items-center gap-2" data-testid="targets-config-reviews-row">
                      <label className="label-small-caps flex items-center gap-1" htmlFor="targets-config-reviews">
                        Reviews / week (studio)
                        <InfoTip>Weekly new-review goal for this whole studio. The reviews tile turns green when it is met.</InfoTip>
                      </label>
                      <input
                        id="targets-config-reviews"
                        type="number"
                        min={0}
                        disabled={!canEditTargets}
                        data-testid="targets-config-reviews"
                        value={reviewsFor(sid)}
                        onChange={(e) => setStudioReviewsTarget(sid, Number(e.target.value) || 0)}
                        className={inputCls}
                      />
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                            <th className="text-left px-2 py-2">Artist</th>
                            <th className="text-right px-2 py-2">
                              <span className="inline-flex items-center gap-1">
                                Sessions
                                <InfoTip align="right">Completed sessions target per week for this artist.</InfoTip>
                              </span>
                            </th>
                            <th className="text-right px-2 py-2">
                              <span className="inline-flex items-center gap-1">
                                Dollars
                                <InfoTip align="right">Weekly dollar (turnover) target for this artist.</InfoTip>
                              </span>
                            </th>
                            <th className="text-right px-2 py-2">
                              <span className="inline-flex items-center gap-1">
                                Deposit sessions
                                <InfoTip align="right">
                                  Weekly booked-sessions-with-a-deposit target. The studio total (summed across artists)
                                  is the DEPOSITS number on the Targets tab.
                                </InfoTip>
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {studioArtists.map((a) => {
                            const t = targetFor(a.id);
                            return (
                              <tr key={a.id} className="border-t border-gold-dim/15" data-testid={`targets-config-row-${a.id}`}>
                                <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{a.displayName}</td>
                                <td className="px-2 py-2 text-right">
                                  <input
                                    type="number"
                                    min={0}
                                    disabled={!canEditTargets}
                                    data-testid={`targets-config-sessions-${a.id}`}
                                    value={t.sessionsPerWeek}
                                    onChange={(e) => setArtistTarget(a.id, { sessionsPerWeek: Number(e.target.value) || 0 })}
                                    className={inputCls}
                                  />
                                </td>
                                <td className="px-2 py-2 text-right">
                                  <input
                                    type="number"
                                    min={0}
                                    disabled={!canEditTargets}
                                    data-testid={`targets-config-dollars-${a.id}`}
                                    value={t.dollarsPerWeek}
                                    onChange={(e) => setArtistTarget(a.id, { dollarsPerWeek: Number(e.target.value) || 0 })}
                                    className={inputCls}
                                  />
                                </td>
                                <td className="px-2 py-2 text-right">
                                  <input
                                    type="number"
                                    min={0}
                                    disabled={!canEditTargets}
                                    data-testid={`targets-config-depsessions-${a.id}`}
                                    value={t.depositSessionsPerWeek}
                                    onChange={(e) => setArtistTarget(a.id, { depositSessionsPerWeek: Number(e.target.value) || 0 })}
                                    className={inputCls}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                          {studioArtists.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="px-2 py-4 font-serif text-cream-soft text-sm">
                                No active artists at this studio.
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="font-serif text-cream-soft text-sm">No studios to show.</p>
                )}
              </div>
            );
          })()
        ) : null}

        {tab === 'audit' ? (
          <div className="panel p-4">
            <p className="font-serif text-cream-soft text-sm">
              Audit trail surfaces who-did-what across customer records. Production-only at this
              stage. In the prototype, the brain mutations are logged to the simulation event bus
              which can be inspected via window.__sim.fired().
            </p>
          </div>
        ) : null}
      </div>

      {editingStudio ? (
        <EditStudioModal
          studio={editingStudio}
          agents={Object.values(agents)}
          onCancel={() => setEditingStudioId(null)}
          onSave={(s) => {
            upsertStudio(s);
            setEditingStudioId(null);
          }}
        />
      ) : null}

      {editingAgent ? (
        <EditTeamMemberModal
          agent={editingAgent}
          studios={Object.values(studios)}
          onCancel={() => setEditingAgentId(null)}
          onSave={(a) => {
            upsertAgent(a);
            setEditingAgentId(null);
          }}
        />
      ) : null}

      {addingStudio ? (
        <AddStudioModal
          agents={Object.values(agents)}
          onCancel={() => setAddingStudio(false)}
          onCreate={(studio) => {
            upsertStudio(studio);
            setAddingStudio(false);
          }}
        />
      ) : null}

      {merchModal ? (
        <MerchItemModal
          item={merchModal.item}
          mode={merchModal.mode}
          onCancel={() => setMerchModal(null)}
          onSave={(item) => {
            upsertMerchItem(item);
            setMerchModal(null);
          }}
        />
      ) : null}
    </section>
  );
}
